import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Search } from '@components/atoms';
import { cn } from '@utils';

interface InputProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'color'>,
		VariantProps<typeof inputVariants> {
	width?: number | string;
	textCenter?: boolean;
	wrapperClassName?: string;
}

const DEFAULT_WIDTH = 250;

const wrapperVariants = cva('flex items-center rounded-md', {
	variants: {
		variant: {
			default: 'bg-bgFields p-2',
			search: 'bg-bgFields p-2',
			naked: 'p-0',
		},
	},
	defaultVariants: {
		variant: 'default',
	},
});

const inputVariants = cva('m-0 w-full border-none bg-transparent p-0 leading-6 focus:outline-hidden', {
	variants: {
		variant: {
			default: 'disabled:bg-disabled disabled:text-disabled',
			search: 'disabled:bg-disabled disabled:text-disabled',
			naked: '',
		},
		size: {
			xs: 'text-xs',
			sm: 'text-sm',
			md: 'text-base',
			lg: 'text-lg',
			xl: 'text-xl',
		},
		weight: {
			normal: 'font-normal',
			medium: 'font-medium',
			bold: 'font-semibold',
		},
		color: {
			primary: 'text-primary',
			secondary: 'text-secondary',
			black: 'text-textBlack',
			darkGray: 'text-textDarkGray',
			mediumGray: 'text-textMediumGray',
			success: 'text-success',
			accent: 'text-accent',
			disabled: 'text-disabled',
			error: 'text-error',
			white: 'text-white',
			info: 'text-info',
		},
	},
	defaultVariants: {
		variant: 'default',
		size: 'md',
		weight: 'normal',
		color: 'primary',
	},
});

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
	(
		{
			disabled = false,
			onChange,
			placeholder,
			type = 'text',
			value,
			variant,
			width,
			textCenter,
			wrapperClassName,
			size,
			weight,
			color,
			className,
			...props
		},
		ref
	) => {
		width ??= variant === 'naked' ? 0 : DEFAULT_WIDTH;
		const resolvedWrapperClassName = cn(wrapperVariants({ variant }), wrapperClassName);
		const inputClassName = inputVariants({ variant, size, weight, color });

		return (
			<div
				className={resolvedWrapperClassName}
				style={width ? { width: typeof width === 'number' ? `${width}px` : width } : undefined}
			>
				<input
					{...props}
					ref={ref}
					className={cn(inputClassName, textCenter ? 'text-center' : '', className)}
					disabled={disabled}
					onChange={onChange}
					placeholder={placeholder}
					type={type}
					value={value}
				/>
				{variant === 'search' && (
					<div className="ml-2 flex items-center justify-center">
						<Search className="text-secondary" />
					</div>
				)}
			</div>
		);
	}
);

Input.displayName = 'Input';
